import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import useFlags from "@common/hooks/useFlags";
import { ChangeOrder, isLocked } from "../types";

export const CHANGE_ORDERS_PRIVILEGE = "projects.change_orders";

// Every change-order gate in one place. hasPrivilege matches levels by
// membership, so each check lists all the levels it allows.
export const useChangeOrderAccess = () => {
  const { hasPrivilege, privilegesLoading, user } = usePrivilegeCheck();
  const { releaseChangeOrders } = useFlags();
  const flagOn = Boolean(releaseChangeOrders);
  const meId = user?.data?.id ? String(user.data.id) : null;

  const canView = hasPrivilege(CHANGE_ORDERS_PRIVILEGE, [
    "view",
    "create",
    "manage",
  ]);
  const canManage = hasPrivilege(CHANGE_ORDERS_PRIVILEGE, ["manage"]);
  const canCreateAny = hasPrivilege(CHANGE_ORDERS_PRIVILEGE, [
    "create",
    "manage",
  ]);
  const showRolesHint =
    flagOn && !canView && hasPrivilege("settings.roles", ["manage"]);

  // Editing existing change orders isn't gated on the flag; the API doesn't
  // gate update on it.
  const canEdit = (row: Pick<ChangeOrder, "userId">) =>
    canManage ||
    (canCreateAny &&
      meId !== null &&
      row.userId !== null &&
      row.userId === meId);

  const canDelete = (row: Pick<ChangeOrder, "userId" | "status">) =>
    canEdit(row) && !isLocked(row.status);

  return {
    loading: privilegesLoading,
    canView,
    canCreate: flagOn && canCreateAny,
    canEdit,
    canDelete,
    // False when no row could ever be deleted (below create), so the list
    // can drop selection entirely.
    canDeleteAny: canCreateAny,
    showRolesHint,
    navVisible: canView || showRolesHint,
  };
};

export default useChangeOrderAccess;
